import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  model,
  ModelSignal,
  OnDestroy,
  output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { AbbStepperComponent } from '@components/abb-stepper/abb-stepper.component';
import { FilterOption } from '@components/abb-table/abb-table.component';
import {
  deviceListQuery,
  deviceSelectOptionsWImageQuery,
} from '@graphql/asset-management';
import { getWidgetMetricTypesQuery } from '@graphql/dashboard';
import { groupSelectOptionsWImageQuery } from '@graphql/group';
import { propertySelectOptionsWImageQuery } from '@graphql/property';
import { userListQuery } from '@graphql/user';
import { TranslateService } from '@ngx-translate/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { ToastService } from '@services/ui/toast.service';
import { Apollo } from 'apollo-angular';
import { ChangeDetectorRef } from '@angular/core';
import { AbbCommonModule } from 'app/abb-common.module';
import {
  AlarmCondition,
  AlarmListQuery,
  ConditionTypes,
  DeviceSelectOptionsWImageQuery,
  DeviceSelectOptionsWImageQueryVariables,
  GetWidgetMetricTypesQuery,
  GetWidgetMetricTypesQueryVariables,
  GroupSelectOptionsWImageQuery,
  GroupSelectOptionsWImageQueryVariables,
  PeriodOptions,
  PropertySelectOptionsWImageQuery,
  PropertySelectOptionsWImageQueryVariables,
  EntityType,
  StatisticOptions,
  User,
  UserListQuery,
  WidgetMetricType,
  AlarmCategory,
  DeviceListQuery,
  DeviceListQueryVariables,
  GroupSelectOptionsQuery,
  PropertySelectOptionsQuery,
  GroupSelectOptionsQueryVariables,
  Status,
} from 'gql/graphql';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { validateForm } from '@utils/validation';
import { t } from '@utils/strings';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MatButtonToggle,
  MatButtonToggleModule,
} from '@angular/material/button-toggle';
import {
  WidgetCriteria,
  WidgetDisplayOption,
  WidgetType,
} from '@constants/widgets';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { getErrorMessage } from '@utils/errors';

interface AssetForm extends Record<string, AbstractControl<any, any>> {
  assetType: FormControl<EntityType | null>;
  assets: FormControl<Array<string> | null>;
  propertiesCriteria: FormControl<string | null>;
  groupsCriteria: FormControl<string | null>;
  combinedCriteria: FormControl<{type: 'property' | 'group', id: string, name: string} | null>;
}

interface NotificationsForm extends Record<string, AbstractControl<any, any>> {
  contacts: FormControl<User[] | null>;
}

interface ConditionForm extends Record<string, AbstractControl<any, any>> {
  delay: FormControl<number | null>;
  category: FormControl<AlarmCategory | null>;
  type: FormControl<ConditionTypes | null>;
  metric: FormControl<WidgetMetricType | null>;
  statistic: FormControl<StatisticOptions | null>;
  period: FormControl<PeriodOptions | null>;
  thresholdType: FormControl<string | null>;
  alarmCondition: FormControl<AlarmCondition | null>;
  thresholdValue: FormControl<number | null>;
}

interface NameDescForm extends Record<string, AbstractControl<any, any>> {
  name: FormControl<string | null>;
  description: FormControl<string | null>;
}

export interface Device {
  __typename?: 'Device';
  id: string;
  name: string;
  status: Status;
  property: {
    __typename?: 'PropertyWithConsumption' | 'Property';
    name: string;
    id: string;
  };
  groupsCriteria: {
    id: string;
    name: string;
  }[];
  category?: {
    id: string;
    name: string;
    __typename?: string;
  };
  communicationProtocol?: {
    id: string;
    name: string;
    __typename?: string;
  };
}

export type AlarmForm = {
  assets: FormGroup<AssetForm>;
  condition: FormGroup<ConditionForm>;
  notifications: FormGroup<NotificationsForm>;
  nameDesc: FormGroup<NameDescForm>;
};

@Component({
  selector: 'abb-alarm-form',
  standalone: true,
  imports: [
    AbbStepperComponent,
    AbbCommonModule,
    TPipe,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    CommonModule,
    MatChipsModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatButtonToggleModule,
    MatAutocompleteModule,
  ],
  host: {
    class: 'flex flex-1',
  },
  styles: `
    .dotted-box {
      border: 3px dotted;
      margin: 0 0 0 1rem;
    }
    .selected-light-primary.mdc-list-item.mdc-list-item--selected {
      background-color: rgb(var(--abb-color-primary-light) / 0.25);
      border-top: 1px solid rgb(var(--abb-color-primary-light) / 0.4);
      border-bottom: 1px solid rgb(var(--abb-color-primary-light) / 0.4);
    }
    .font-medium-label {
      --mat-option-label-text-color: var(--abb-color-black);
      --mat-option-label-text-weight: 500;
      --mat-option-selected-state-label-text-color: var(--abb-color-black);
    }
  `,
  template: `
    <abb-stepper
      [stepsControls]="stepsControls()"
      [stepsLabels]="stepsLabels()"
      [isVertical]="true"
      [isLoading]="isLoading()"
      [showStepLabelsInDots]="false"
      [stepperTitle]="(mode() === 'new' ? 'New Alarm' : 'Edit Alarm') | t"
      (onValidationError)="showError($event)"
      (onCancel)="cancel()"
    >
      <ng-template #stepContent let-index="index">
        <form id="alarm-form" [formGroup]="form" (ngSubmit)="onSubmit()">
          @if(index === 0) {
          <div formGroupName="assets" class="flex flex-col gap-2">
            <mat-form-field class="w-full">
              <mat-label>{{ 'Properties / Groups' | t }}</mat-label>
              <mat-select
                formControlName="combinedCriteria"
                [panelClass]="'panel'"
                (selectionChange)="onCombinedCriteriaChange($event.value)"
                [compareWith]="compareItems"
              >
                <mat-optgroup label="{{ 'Properties' | t }}">
                  @for (item of combinedCriteria(); track item.id) {
                    @if (item.type === 'property') {
                      <mat-option [value]="item">
                        {{ item.name }}
                      </mat-option>
                    }
                  }
                </mat-optgroup>
                <mat-optgroup label="{{ 'Groups' | t }}">
                  @for (item of combinedCriteria(); track item.id) {
                    @if (item.type === 'group') {
                      <mat-option [value]="item">
                        {{ item.name }}
                      </mat-option>
                    }
                  }
                </mat-optgroup>
              </mat-select>
            </mat-form-field>
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Devices' | t }}
            </h3>
            <ng-container
              *ngTemplateOutlet="
                multiAutocompleteTemplate;
                context: {
                  $implicit: {
                    selectedItems: selectedDevices,
                    disabled: false,
                    name: 'devices',
                    model: searchCtrl.value,
                    filteredItems: availableDevices,
                    label: ('Devices' | t),
                  }
                }
              "
            ></ng-container>
          </div>
          } @else if (index === 1) {
          <div
            id="condition"
            formGroupName="condition"
            class="flex flex-col gap-2 w-full max-w-full"
          >
            <div class="flex flex-col gap-2 w-full">
              <h3 class="text-base font-semibold text-abb-font/85">
                {{ conditionTypeLabel() | t }}
              </h3>
              <mat-form-field class="w-full">
                <mat-select
                  [compareWith]="compareItems"
                  formControlName="metric"
                  id="metric"
                  name="metric"
                  [panelClass]="'panel'"
                >
                  @for (metricType of metricTypeOptions$ | async; track
                  metricType.id) {
                  <mat-option [id]="metricType.id" [value]="metricType">{{
                    metricType.name | t
                  }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              @if (form.controls.condition.controls.metric.value) {
              <div class="flex flex-col gap-5">
                <div class="flex flex-col gap-3">
                  <div class="flex flex-col">
                    <h3 class="text-base font-semibold text-abb-font/85">
                      {{
                        'Whenever {metric} is'
                          | t
                            : {
                                metric: (
                                  form.controls.condition.controls.metric.value
                                    .name | t
                                )?.toLowerCase()
                              }
                      }}
                      ...
                    </h3>
                    <mat-hint class="text-sm text-abb-font/65">
                      {{ 'Define alarm condition' | t }}
                    </mat-hint>
                  </div>
                  <div class="flex flex-row">
                    <mat-radio-group
                      id="alarmCondition"
                      formControlName="alarmCondition"
                      aria-label="Select an option"
                      color="primary"
                      class="flex flex-col gap-2 pb-5"
                    >
                      @for(option of alarmConditions(); track option.value){
                      <div class="flex flex-col relative">
                        <mat-radio-button
                          [id]="option.value"
                          [value]="option.value"
                          class="border-font-radio-buttons flex-1"
                        >
                          <div class="flex flex-col">
                            <label
                              class="text-base"
                              [ngClass]="{
                                'font-medium':
                                  form.controls.condition.controls
                                    .alarmCondition.value === option.value
                              }"
                              >{{
                                option.skipTranslation
                                  ? option.label
                                  : (option.label | t)
                              }}
                            </label>
                          </div>
                        </mat-radio-button>
                        <mat-hint
                          class="absolute -bottom-3 left-10 text-sm text-abb-font/65"
                        >
                          {{ option.symbol }} {{ 'threshold' | t }}
                        </mat-hint>
                      </div>
                      }
                    </mat-radio-group>
                  </div>
                  <div class="flex flex-1 flex-col gap-3">
                    <div class="flex flex-col">
                      <h3 class="text-base font-semibold text-abb-font/85">
                        {{ 'than' | t }} ...
                      </h3>
                      <mat-hint class="text-sm text-abb-font/65">
                        {{ 'Define threshold' | t }}
                      </mat-hint>
                    </div>
                    <mat-form-field class="w-full">
                      <input
                        id="thresholdValue"
                        formControlName="thresholdValue"
                        type="number"
                        matInput
                        placeholder="123"
                      />
                      <mat-hint class="text-abb-font/65">{{
                        'Must be a number' | t
                      }}</mat-hint>
                    </mat-form-field>
                  </div>
                </div>
                <div class="flex flex-1 flex-col gap-3">
                  <div class="flex flex-col">
                    <h3 class="text-base font-semibold text-abb-font/85">
                      {{ 'delay' | t }}
                    </h3>
                    <mat-hint class="text-sm text-abb-font/65">
                      {{ 'Define delay' | t }}
                    </mat-hint>
                  </div>
                  <mat-form-field class="w-full">
                    <input
                      id="delay"
                      formControlName="delay"
                      type="number"
                      matInput
                      placeholder="123"
                      min="0"
                      max="999"
                    />
                    <mat-hint class="text-abb-font/65">{{
                      'Must be a number' | t
                    }}</mat-hint>
                  </mat-form-field>
                </div>
                <h3 class="text-base font-semibold text-abb-font/85">
                  {{ 'Category' | t }}
                </h3>
                <mat-form-field class="w-full">
                  <mat-select
                    formControlName="category"
                    id="category"
                    name="category"
                    [panelClass]="'panel'"
                  >
                    @for (category of categoryOptions(); track category.value) {
                    <mat-option
                      [id]="category.value.toString()"
                      [value]="category.value"
                      >{{ category.label | t }}</mat-option
                    >
                    }
                  </mat-select>
                </mat-form-field>
              </div>
              }
            </div>
          </div>
          } @else if (index === 2) {
          <div
            id="notifications"
            formGroupName="notifications"
            class="flex flex-col gap-2"
          >
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Notification title' | t }}
            </h3>
            <mat-form-field class="w-full" color="primary">
              <mat-label>{{ 'Select Contact' | t }}</mat-label>
              <mat-select
                [compareWith]="compareItems"
                formControlName="contacts"
                id="contacts"
                multiple
                [panelClass]="'panel border-font-checkbox'"
              >
                @for (contact of contacts$ | async; track contact.id) {
                <mat-option [id]="'contacts-' + contact.id" [value]="contact">
                  {{ contact.name }} {{ contact.lastName }}
                  <small>({{ contact.email }})</small>
                </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          } @else if (index === 3) {
          <div
            id="nameDesc"
            formGroupName="nameDesc"
            class="flex flex-col gap-2 w-full max-w-full"
          >
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Name' | t }}
            </h3>
            <mat-form-field class="w-full">
              <input
                id="name"
                formControlName="name"
                type="text"
                matInput
                placeholder="Name"
              />
            </mat-form-field>
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Description' | t }}
            </h3>
            <mat-form-field class="w-full">
              <textarea
                id="description"
                formControlName="description"
                matInput
              ></textarea>
            </mat-form-field>
          </div>
          }
          <ng-template #multiAutocompleteTemplate let-item>
            <div>
              <mat-form-field class="w-full">
                <mat-label>{{ item.label }}</mat-label>
                <mat-chip-grid #chipGrid [disabled]="item.disabled">
                  @for (el of item.selectedItems(); track el) {
                  <mat-chip-row
                    (removed)="removeItem(item.selectedItems, el)"
                    class="chips"
                  >
                    {{ el.name | t }}
                    <button matChipRemove type="button">
                      <abb-icon
                        size="small"
                        name="mat-outlined/close"
                      ></abb-icon>
                    </button>
                  </mat-chip-row>
                  }
                </mat-chip-grid>
                <input
                  type="text"
                  [name]="item.name"
                  matInput
                  [(ngModel)]="item.model"
                  [ngModelOptions]="{ standalone: true }"
                  [matAutocomplete]="devicesAutocomplete"
                  [matChipInputFor]="chipGrid"
                  [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                  (matChipInputTokenEnd)="
                    onAdd($event, availableDevices, item.selectedItems, item.model)
                  "
                  [disabled]="item.selectedItems().length >= 10"
                  #ref
                />
                <mat-autocomplete
                  #devicesAutocomplete="matAutocomplete"
                  [displayWith]="autocompleteDisplayWith"
                  (optionSelected)="
                    onSelect($event, item.selectedItems, item.model, ref)
                  "
                  [hideSingleSelectionIndicator]="true"
                  class="panel"
                >
                  @for (el of availableDevices(); track el.id) {
                  <mat-option
                    [value]="el"
                    [disabled]="item.selectedItems().length >= 10"
                    >{{ el.name | t }}</mat-option
                  >
                  }
                </mat-autocomplete>
                @if ( item.selectedItems().length >= 10) {
                <mat-hint class="text-sm text-orange-600 mt-1">
                  {{ 'Maximum 10 devices can be selected' | t }}
                </mat-hint>
                }
              </mat-form-field>
            </div>
          </ng-template>
          <ng-template #singleAutocompleteTemplate let-item>
            <div>
              <mat-form-field class="w-full">
                <mat-label>{{ item.label }}</mat-label>
                <input
                  type="text"
                  [name]="item.name"
                  matInput
                  [(ngModel)]="item.model"
                  [ngModelOptions]="{ standalone: true }"
                  [matAutocomplete]="singleAutocomplete"
                  [required]="item.required"
                />
                <mat-autocomplete
                  #singleAutocomplete="matAutocomplete"
                  [displayWith]="autocompleteDisplayWith"
                  class="panel"
                >
                  @for (option of item.options(); track option.id) {
                  <mat-option [value]="option">{{ option.name }}</mat-option>
                  }
                </mat-autocomplete>
                <mat-error
                  *ngIf="form.controls?.assets?.controls?.[item.name]?.invalid"
                  >{{ 'This field is required' | t }}</mat-error
                >
              </mat-form-field>
            </div>
          </ng-template>
        </form>
      </ng-template>
      <ng-template #lastStepBtn>
        <abb-button
          class="w-32"
          id="alarm-form-submit"
          [type]="'submit'"
          [loading]="isLoading()"
          [disabled]="form.invalid || isLoading()"
          form="alarm-form"
          >{{ (mode() === 'new' ? 'Create' : 'Save') | t }}</abb-button
        >
      </ng-template>
    </abb-stepper>
  `,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'alarms.tabs.list',
    },
  ],
})
export class AlarmFormComponent implements OnDestroy {
  public EntityType = EntityType;
  readonly WidgetType = WidgetType;
  readonly WidgetCriteria = WidgetCriteria;
  mode = input<'new' | 'edit'>();
  isLoading = input<boolean>(false);
  formData = input<AlarmListQuery['alarmList'][number]>();
  formSubmit = output<FormGroup>();
  form: FormGroup<AlarmForm> = new FormGroup({
    assets: new FormGroup({
      assetType: new FormControl<EntityType | null>(null, [
        Validators.required,
      ]),
      assets: new FormControl<Array<string>>(new Array(), [
        Validators.required,
      ]),
      propertiesCriteria: new FormControl<string | null>(null), // singola stringa
      groupsCriteria: new FormControl<string | null>(null), // singola stringa
      combinedCriteria: new FormControl<{type: 'property' | 'group', id: string, name: string} | null>(null),
    }),

    condition: new FormGroup({
      type: new FormControl<ConditionTypes | null>(null, [Validators.required]),
      delay: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(0),
        Validators.max(999),
      ]),
      category: new FormControl<AlarmCategory | null>(null, [
        Validators.required,
      ]),
      metric: new FormControl<WidgetMetricType | null>(null, [
        Validators.required,
      ]),
      statistic: new FormControl<StatisticOptions | null>(null),
      period: new FormControl<PeriodOptions | null>(null),
      thresholdType: new FormControl<string | null>('Static'),
      alarmCondition: new FormControl<AlarmCondition | null>(null, [
        Validators.required,
      ]),
      thresholdValue: new FormControl<number | null>(null, [
        Validators.required,
      ]),
    }),
    notifications: new FormGroup({
      contacts: new FormControl<User[] | null>([], [Validators.required]),
    }),
    nameDesc: new FormGroup({
      name: new FormControl<string | null>(null, [Validators.required]),
      description: new FormControl<string | null>(null, [Validators.required]),
    }),
  });
  filteredProperties = computed(() => {
    const propertyQuery = this.propertiesQuery()?.toLowerCase() ?? '';
    return this.propertiesCriteria().filter(
      (d) =>
        d.name.toLowerCase().includes(propertyQuery) &&
        !this.selectedProperties().some((sel) => sel.id === d.id)
    );
  });
  filteredGroups = computed(() => {
    const groupQuery = this.groupsQuery()?.toLowerCase() ?? '';
    return this.groupsCriteria().filter(
      (d) =>
        d.name.toLowerCase().includes(groupQuery) &&
        !this.selectedGroups().some((sel) => sel.id === d.id)
    );
  });
  groupsQuery = model<string | undefined>('');
  propertiesQuery = model<string | undefined>('');
  groupsCriteria = signal<GroupSelectOptionsQuery['groupList']>([]);
  propertiesCriteria = signal<PropertySelectOptionsQuery['propertyList']>([]);
  combinedCriteria = computed(() => {
    const properties = this.propertiesCriteria().map(prop => ({
      type: 'property' as const,
      id: prop.id,
      name: prop.name,
      displayName: `Property: ${prop.name}`
    }));
    const groups = this.groupsCriteria().map(group => ({
      type: 'group' as const,
      id: group.id,
      name: group.name,
      displayName: `Group: ${group.name}`
    }));
    return [...properties, ...groups];
  });
  separatorKeysCodes: number[] = [ENTER, COMMA];
  selectedDevices = signal<DeviceListQuery['deviceList']>([]);
  selectedGroups = signal<{ id: string; name: string }[]>([]);

  deviceList: DeviceListQuery['deviceList'] = []; // array di dispositivi
  filteredDevices = signal<Device[]>([]);

  stepsControls = computed(() => [
    this.form.controls.assets,
    this.form.controls.condition,
    this.form.controls.notifications,
    this.form.controls.nameDesc,
  ]);
  stepsLabels = computed(() => [
    t(this.prefix, 'Select assets', this.translate),
    t(this.prefix, 'Select Trigger Criteria', this.translate),
    t(this.prefix, 'Configure Notifications', this.translate),
    t(this.prefix, 'Add Name and Description', this.translate),
  ]);
  assetTypes = signal<FilterOption[]>([
    { value: EntityType.Property, label: 'Properties', skipTranslation: true },
    { value: EntityType.Group, label: 'Groups', skipTranslation: true },
    { value: EntityType.Device, label: 'Devices', skipTranslation: true },
  ]);
  conditionTypes$ = new BehaviorSubject<FilterOption[]>([
    { value: ConditionTypes.Metric, label: 'Metric', skipTranslation: true },
  ]);
  statisticOptions = signal<FilterOption[]>([
    {
      value: StatisticOptions.Average,
      label: 'Average',
      skipTranslation: true,
    },
  ]);
  periodOptions = signal<FilterOption[]>([
    { value: PeriodOptions.Day, label: '24 Hours', skipTranslation: true },
    { value: PeriodOptions.Halfday, label: '12 Hours', skipTranslation: true },
  ]);
  categoryOptions = signal<FilterOption[]>([
    { value: AlarmCategory.Critical, label: 'Critical' },
    { value: AlarmCategory.Warning, label: 'Warning' },
  ]);
  alarmConditions = signal<any[]>([
    {
      value: AlarmCondition.Greater,
      label: 'Greater',
      skipTranslation: true,
      symbol: '> ',
    },
    {
      value: AlarmCondition.Greaterequal,
      label: 'Greater/Equal',
      skipTranslation: true,
      symbol: '>= ',
    },
    {
      value: AlarmCondition.Lowerequal,
      label: 'Lower/Equal',
      skipTranslation: true,
      symbol: '<= ',
    },
    {
      value: AlarmCondition.Lower,
      label: 'Lower',
      skipTranslation: true,
      symbol: '< ',
    },
  ]);
  contacts$ = new BehaviorSubject<User[]>([]);
  criteria = model<WidgetCriteria>();
  criteriaOptions = computed(() => [
    { value: WidgetCriteria.GROUP, label: 'Groups' },
    { value: WidgetCriteria.PROPERTY, label: 'Properties' },
  ]);
  activeCriteria = computed(() => {
    const groupsCriteria = this.form.controls.assets.controls.groupsCriteria.value;
    const propertiesCriteria = this.form.controls.assets.controls.propertiesCriteria.value;
    if (this.mode() === 'edit') {
      return null;
    }
    if (groupsCriteria) {
      return WidgetCriteria.GROUP;
    } else if (propertiesCriteria) {
      return WidgetCriteria.PROPERTY;
    }
    return null;
  });

  selectedProperty = model<
    | {
        id: string;
        name: string;
        __typename?: string;
      }
    | string
    | null
  >(null);
  selectedDevice = model<
    | {
        id: string;
        name: string;
        __typename?: string;
      }
    | string
    | null
  >(null);
  selectedGroup = model<
    | {
        id: string;
        name: string;
        propertiesCriteria: {
          id: string;
        }[];
        __typename?: string;
      }
    | string
    | null
  >(null);
  display = model<WidgetDisplayOption>();
  assetLabel = signal<string | null>(null);
  allDevices = signal<Device[]>([]);
  private normalizeDevice(device: Device): {
    __typename?: 'Device';
    id: string;
    name: string;
    status: Status;
    property: {
      __typename?: 'PropertyWithConsumption';
      name: string;
      id: string;
    };
    groupsCriteria: {
      __typename?: 'Group';
      name: string;
      id: string;
    }[];
    category: {
      __typename?: 'Category';
      name: string;
      id: string;
    };
    communicationProtocol: {
      __typename?: 'CommunicationProtocol';
      name: string;
      id: string;
    };
  } {
    return {
      ...device,
      __typename: 'Device',
      property: device.property
        ? { ...device.property, __typename: 'PropertyWithConsumption' }
        : { __typename: 'PropertyWithConsumption', name: '', id: '' },
      groupsCriteria: Array.isArray(device.groupsCriteria)
        ? device.groupsCriteria.map((g) => ({ ...g, __typename: 'Group' }))
        : [],
      category: device.category
        ? { ...device.category, __typename: 'Category' }
        : { __typename: 'Category', name: '', id: '' },
      communicationProtocol: device.communicationProtocol
        ? {
            ...device.communicationProtocol,
            __typename: 'CommunicationProtocol',
          }
        : { __typename: 'CommunicationProtocol', name: '', id: '' },
    };
  }
  selectedProperties = signal<{ id: string; name: string }[]>([]);
  devices = signal<
    Array<{
      __typename?: 'Device';
      id: string;
      name: string;
      status: Status;
      property: {
        __typename?: 'PropertyWithConsumption';
        name: string;
        id: string;
      };
      groupsCriteria: {
        __typename?: 'Group';
        name: string;
        id: string;
      }[];
      category?: {
        __typename?: 'Category';
        name: string;
        id: string;
      };
      communicationProtocol?: {
        __typename?: string;
        name: string;
        id: string;
      };
    }>
  >([]);
  selectedDeviceIds = signal<string[]>([]);
  availableDevices = computed(() => {
    const allDevices = this.devices();
    const selectedIds = this.selectedDeviceIds();
    
    return allDevices.filter(device => !selectedIds.includes(device.id));
  });
  isLoadingAssets = signal<boolean>(false);
  conditionTypeLabel = signal<string | null>('Metric');
  searchCtrl = new FormControl<string>('');
  assets$ = new BehaviorSubject<
    Array<Record<string, string | null | undefined>>
  >([]);
  filteredAssets = signal<Array<string>>([]);
  deviceSelectOptions = signal<FilterOption[]>([]);
  onCancel = output<void>();
  metricTypeOptions$ = new BehaviorSubject<
    GetWidgetMetricTypesQuery['getWidgetMetricTypes']
  >([]);
  groupOptions = computed(() => {
    const query = this.groupsQuery()?.toLowerCase() ?? '';
    return [
      {
        name: 'Groups',
        items: this.groupsCriteria().filter((g) =>
          !query ? true : g.name.toLowerCase().includes(query)
        ),
      },
    ];
  });
  propertyOptions = computed(() => {
    const query = this.propertiesQuery()?.toLowerCase() ?? '';
    return [
      {
        name: 'Properties',
        items: this.propertiesCriteria().filter((p) =>
          !query ? true : p.name.toLowerCase().includes(query)
        ),
      },
    ];
  });
  private readonly subscriptions: Subscription[] = [];
  private readonly prefix = inject(TRANSLATION_PREFIX);
  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    this.groupsControl.valueChanges.subscribe(() => this.filterDevices());
    this.propertyControl.valueChanges.subscribe(() => this.filterDevices());

    this.subscriptions.push(
      this.searchCtrl.valueChanges.subscribe((searchQuery: string | null) => {
        this.filteredAssets.set(
          searchQuery
            ? this._filter(searchQuery)
            : this.assets$.value.map((a) => a['value']!).slice()
        );
      }),
      this.form.controls.assets.valueChanges.subscribe((newassets) => {
        const assets = this.form.controls.assets.controls.assets.value;
        const assetType = this.form.controls.assets.controls.assetType?.value;
        const selectedDeviceIds = this.selectedDevices().map(d => d.id);
        const availableAssets = Array.isArray(assets) && assets.length > 0 ? assets : selectedDeviceIds;
        
        if (
          availableAssets.length > 0 &&
          this.form.controls.condition.controls.type.value === ConditionTypes.Metric
        ) {
          const metricAssetType = EntityType.Device;
          
          apollo
            .watchQuery<
              GetWidgetMetricTypesQuery,
              GetWidgetMetricTypesQueryVariables
            >({
              query: getWidgetMetricTypesQuery,
              variables: {
                assets: {
                  assetType: metricAssetType,
                  assets: availableAssets,
                },
              },
            })
            .refetch({
              assets: {
                assetType: metricAssetType,
                assets: availableAssets,
              },
            });

          apollo
            .watchQuery<
              GetWidgetMetricTypesQuery,
              GetWidgetMetricTypesQueryVariables
            >({
              query: getWidgetMetricTypesQuery,
              variables: {
                assets: {
                  assetType: metricAssetType,
                  assets: availableAssets,
                },
              },
            })
            .valueChanges.subscribe({
              next: ({ data }) => {
                this.metricTypeOptions$.next(data.getWidgetMetricTypes);
              },
              error: (err) => {},
            });
        }
      }),
      this.form.controls.assets.controls.assetType.valueChanges.subscribe(
        (assetType) => {
          this.isLoadingAssets.set(true);
          this.form.controls.assets.controls.assets.reset();

          this.assetLabel.set(
            this.assetTypes().find((t) => t.value === assetType)?.label ?? ''
          );

          if (assetType === EntityType.Property) {
            apollo
              .watchQuery<
                PropertySelectOptionsWImageQuery,
                PropertySelectOptionsWImageQueryVariables
              >({
                query: propertySelectOptionsWImageQuery,
              })
              .valueChanges.subscribe({
                next: ({ data }) => {
                  const propertyList = data.propertyList.map((property) => ({
                    value: property.id,
                    label: property.name,
                    image: property.imgUrl,
                  }));
                  this.assets$.next(propertyList);
                  this.filteredAssets.set(propertyList.map((p) => p.value));
                  this.isLoadingAssets.set(false);
                },
                error: () => this.isLoadingAssets.set(false),
              });
          } else if (assetType === EntityType.Device) {
            apollo
              .watchQuery<
                DeviceSelectOptionsWImageQuery,
                DeviceSelectOptionsWImageQueryVariables
              >({
                query: deviceSelectOptionsWImageQuery,
              })
              .valueChanges.subscribe({
                next: ({ data }) => {
                  const deviceWImgList = data.deviceList.map((device) => ({
                    value: device.id,
                    label: device.name,
                    image: device.imgUrl,
                  }));
                  this.assets$.next(deviceWImgList);
                  this.filteredAssets.set(deviceWImgList.map((d) => d.value));
                  this.isLoadingAssets.set(false);
                },
                error: () => this.isLoadingAssets.set(false),
              });
          } else if (assetType === EntityType.Group) {
            apollo
              .watchQuery<
                GroupSelectOptionsWImageQuery,
                GroupSelectOptionsWImageQueryVariables
              >({
                query: groupSelectOptionsWImageQuery,
              })
              .valueChanges.subscribe({
                next: ({ data }) => {
                  const groupList = data.groupList.map((group) => ({
                    value: group.id,
                    label: group.name,
                    image: group.imgUrl,
                  }));
                  this.assets$.next(groupList);
                  this.filteredAssets.set(groupList.map((g) => g.value));
                  this.isLoadingAssets.set(false);
                },
                error: () => this.isLoadingAssets.set(false),
              });
          } else {
            apollo
              .watchQuery<
                DeviceSelectOptionsWImageQuery,
                DeviceSelectOptionsWImageQueryVariables
              >({
                query: deviceSelectOptionsWImageQuery,
              })
              .valueChanges.subscribe({
                next: ({ data }) => {
                  const deviceWImgList = data.deviceList.map((device) => ({
                    value: device.id,
                    label: device.name,
                    image: device.imgUrl,
                  }));

                  this.deviceSelectOptions.set(deviceWImgList); // ✅ solo per la select
                  this.isLoadingAssets.set(false);
                },
                error: () => this.isLoadingAssets.set(false),
              });
          }
        }
      ),
      apollo
        .watchQuery<UserListQuery>({
          query: userListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.contacts$.next(data.userList);
          },
          error: (err) => {},
        }),
      apollo
        .watchQuery<GroupSelectOptionsQuery, GroupSelectOptionsQueryVariables>({
          query: groupSelectOptionsWImageQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.groupsCriteria.set(data.groupList);
            if (this.mode() === 'edit' && this.formData()) {
              this.updateCombinedCriteriaFromFormData();
            }
          },
          error: (err) => {},
        }),
      apollo
        .watchQuery<
          PropertySelectOptionsWImageQuery,
          PropertySelectOptionsWImageQueryVariables
        >({
          query: propertySelectOptionsWImageQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.propertiesCriteria.set(data.propertyList);
            if (this.mode() === 'edit' && this.formData()) {
              this.updateCombinedCriteriaFromFormData();
            }
          },
          error: (err) => {},
        })
    );
  }

  ngOnInit() {
    if (this.mode() === 'edit') {
      setTimeout(() => {
        this.populateForm();
      }, 150);
    } else {
      this.form.patchValue({
        assets: {
          assetType: EntityType.Device ?? null,
        },
        condition: {
          type: ConditionTypes.Metric,
        },
      });
    }
    this.subscriptions.push(
      this.apollo
        .watchQuery<DeviceListQuery, DeviceListQueryVariables>({
          query: deviceListQuery,
          variables: {
            includeGateway: false,
          },
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            const normalizedDevices = data.deviceList.map((d) =>
              this.normalizeDevice({
                ...d,
                groupsCriteria: (d as any).groups ?? []
              })
            );
            this.devices.set(normalizedDevices);
            this.allDevices.set(normalizedDevices);
          },
          error: (err) => {
            let errMsg = getErrorMessage(err, this.translate);
            this.toast.notifyError(errMsg);
          },
        })
    );
    this.selectedGroup.subscribe((group: any) => {
      if (group && typeof group !== 'string') {
        this.form.controls.assets.controls.groupsCriteria.setValue(group.id);
        const filteredDevices = this.allDevices()
          .filter(
            (device: any) =>
              Array.isArray(device.groupsCriteria) &&
              device.groupsCriteria.some((g: any) => g.id === group.id)
          )
          .map((d) => this.normalizeDevice(d));
        this.devices.set(filteredDevices);
        this.selectedDevices.set(
          this.selectedDevices().filter((d) =>
            filteredDevices.some((fd) => fd.id === d.id)
          )
        );
        // Aggiorna selectedDeviceIds per riflettere i dispositivi ancora validi (gruppi)
        this.selectedDeviceIds.set(
          this.selectedDevices().map(d => d.id)
        );
      } else {
        this.form.controls.assets.controls.groupsCriteria.setValue(null);
        const allNormalized = this.allDevices().map((d) =>
          this.normalizeDevice(d)
        );
        this.devices.set(allNormalized);
      }
    });
    this.selectedProperty.subscribe((property) => {
      if (property && typeof property !== 'string') {
        const filteredDevices = this.allDevices()
          .filter(
            (device: any) =>
              device.property && device.property.id === property.id
          )
          .map((d) => this.normalizeDevice(d));
        this.devices.set(filteredDevices);
        this.selectedDevices.set(
          this.selectedDevices().filter((d) =>
            filteredDevices.some((fd) => fd.id === d.id)
          )
        );
        // Aggiorna selectedDeviceIds per riflettere i dispositivi ancora validi (properties)
        this.selectedDeviceIds.set(
          this.selectedDevices().map(d => d.id)
        );
      } else {
        const allNormalized = this.allDevices().map((d) =>
          this.normalizeDevice(d)
        );
        this.devices.set(allNormalized);
      }
    });
  }

  autocompleteDisplayWith(asset: any) {
    return asset?.name ?? '';
  }
  clearOtherCriteria(selectedType: 'groups' | 'properties'): void {
    if (selectedType === 'groups') {
      this.form.controls.assets.controls.propertiesCriteria.setValue(null);
    } else if (selectedType === 'properties') {
      this.form.controls.assets.controls.groupsCriteria.setValue(null);
    }
    this.form.controls.assets.controls.assets.setValue([]);
    this.selectedDevices.set([]);
    this.selectedDeviceIds.set([]);
    
    this.filterDevices();
  }

  onCombinedCriteriaChange(selected: {type: 'property' | 'group', id: string, name: string} | null): void {
    if (!selected) {
      this.form.controls.assets.controls.propertiesCriteria.setValue(null);
      this.form.controls.assets.controls.groupsCriteria.setValue(null);
    } else if (selected.type === 'property') {
      this.form.controls.assets.controls.propertiesCriteria.setValue(selected.id);
      this.form.controls.assets.controls.groupsCriteria.setValue(null);
    } else if (selected.type === 'group') {
      this.form.controls.assets.controls.groupsCriteria.setValue(selected.id);
      this.form.controls.assets.controls.propertiesCriteria.setValue(null);
    }
    
    this.form.controls.assets.controls.assets.setValue([]);
    this.selectedDevices.set([]);
    this.selectedDeviceIds.set([]);
    
    this.filterDevices();
  }

  updateCombinedCriteriaFromFormData(): void {
    const data = this.formData();
    if (!data) return;

    let combinedCriteria: {type: 'property' | 'group', id: string, name: string} | null = null;
    
    if (data.propertiesCriteria && typeof data.propertiesCriteria === 'string') {
      const property = this.propertiesCriteria().find(p => p.id === data.propertiesCriteria);
      if (property) {
        combinedCriteria = { type: 'property', id: property.id, name: property.name };
      }
    } else if (data.groupsCriteria && typeof data.groupsCriteria === 'string') {
      const group = this.groupsCriteria().find(g => g.id === data.groupsCriteria);
      if (group) {
        combinedCriteria = { type: 'group', id: group.id, name: group.name };
      }
    }
    
    if (combinedCriteria && !this.form.controls.assets.controls.combinedCriteria.value) {
      this.form.controls.assets.controls.combinedCriteria.setValue(combinedCriteria);
    }
  }

  populateForm() {
    const data = this.formData();
    if (!data) return;
    this.form.patchValue(
      {
        assets: {
          propertiesCriteria: typeof data.propertiesCriteria === 'string' ? data.propertiesCriteria : null,
          groupsCriteria: typeof data.groupsCriteria === 'string' ? data.groupsCriteria : null,
          assetType: data.assetType ?? null,
        },
        condition: {
          thresholdType: data.thresholdType ?? null,
          alarmCondition: data.condition ?? null,
          thresholdValue: data.thresholdValue ?? null,
          category: data.category,
          delay: data.delay ?? null,
        },
        nameDesc: {
          name: data.name ?? null,
          description: data.description ?? null,
        },
      },
      { emitEvent: true }
    );
    const setCombinedCriteria = () => {
      let combinedCriteria: {type: 'property' | 'group', id: string, name: string} | null = null;
      
      if (data.propertiesCriteria && typeof data.propertiesCriteria === 'string') {
        const property = this.propertiesCriteria().find(p => p.id === data.propertiesCriteria);
        if (property) {
          combinedCriteria = { type: 'property', id: property.id, name: property.name };
        }
      } else if (data.groupsCriteria && typeof data.groupsCriteria === 'string') {
        const group = this.groupsCriteria().find(g => g.id === data.groupsCriteria);
        if (group) {
          combinedCriteria = { type: 'group', id: group.id, name: group.name };
        }
      }
      
      if (combinedCriteria) {
        this.form.controls.assets.controls.combinedCriteria.setValue(combinedCriteria);
      }
    };

    setCombinedCriteria();
    setTimeout(() => setCombinedCriteria(), 500);

    const assets = data.devices;
    const assetsIds =
      assets && assets.length > 0
        ? assets
            .filter(
              (a): a is { id: string; name: string } =>
                typeof a !== 'string' &&
                !!a &&
                'id' in a &&
                'name' in a
            )
            .map((a) => a.id)
        : null;
    if (assetsIds && assetsIds.length > 0) {
      this.subscriptions.push(
        combineLatest([
          this.assets$.asObservable(),
          this.contacts$.asObservable(),
        ]).subscribe(([assets, contacts]) => {
          this.form.patchValue({
            assets: {
              assets: assets
                .filter((a) => assetsIds?.includes(a['value'] as string))
                ?.map((a) => a['value'] as string),
            },
            notifications: {
              contacts:
                contacts.filter((c) =>
                  data.notificationsContacts?.includes(c.id)
                ) ?? null,
            },
          });
          const selectedIds =
            this.form.controls.assets.controls.assets.value || [];
          const allDevices = this.allDevices();
          const selected = allDevices
            .filter((d) => selectedIds.includes(d.id))
            .map((d) => this.normalizeDevice(d));
          this.selectedDevices.set(
            selected.map((d) => ({
              ...d,
              groupsCriteria: d.groupsCriteria ?? [],
              groups: d.groupsCriteria ?? [],
            }))
          );
          this.selectedDeviceIds.set(selectedIds);
          this.cdRef.detectChanges();
        })
      );
    } else {
      this.subscriptions.push(
        this.contacts$.asObservable().subscribe((contacts) => {
          this.form.patchValue({
            notifications: {
              contacts:
                contacts.filter((c) =>
                  data.notificationsContacts?.includes(c.id)
                ) ?? null,
            },
          });
        })
      );
    }
    this.subscriptions.push(
      this.conditionTypes$.asObservable().subscribe((conditionTypes) => {
        this.form.patchValue({
          condition: {
            type:
              (conditionTypes.find((t) => t.value === data.type)
                ?.value as ConditionTypes) ?? null,
          },
        });
      })
    );

    this.subscriptions.push(
      this.metricTypeOptions$.subscribe((metricTypeOptions) => {
        this.form.patchValue({
          condition: {
            metric:
              metricTypeOptions.find((m) => m.id === data.metric?.id) ?? null,
          },
        });
      })
    );
    setTimeout(() => {
      this.filterDevices();
    }, 100);
  }
  assetSelected(): void {
    this.searchCtrl.setValue(null);
  }
  onAssetRemoved(option: string) {
    const assets = this.form.controls.assets.controls.assets
      .value as Array<string>;
    this.removeFirst(assets, option);
    this.form.controls.assets.controls.assets.setValue(assets);
  }
  assetWith(assetId: string) {
    const found = this.assets$.value.find((p) => p['value'] === assetId);
    return found?.['label'] ?? assetId;
  }
  private _filter(searchQuery: string): Array<string> {
    return this.assets$.value
      .filter((p) =>
        p['label']!.toLowerCase().trim().includes(searchQuery.toLowerCase())
      )
      .map((p) => p['value']!);
  }
  showError(e: any) {
    if (e.containsRequired) {
      this.toast.notifyError(
        t(
          this.prefix,
          `Please fill in all required fields before moving forward`,
          this.translate
        )
      );
    } else if (e.containsInvalid) {
      this.toast.notifyError(
        t(this.prefix, `Some fields are not valid`, this.translate)
      );
    }
  }

  onSubmit(): void {
    const formGroup = this.form.controls.nameDesc;
    validateForm(formGroup, (error) => {
      this.showError({
        containsRequired: error.requiredError,
        containsInvalid: error.invalidError,
      });
    });
    this.formSubmit.emit(this.form);
    console.log(this.form.value);
  }

  compareItems(item1: any, item2: any): boolean {
    return item1 && item2 ? item1.id === item2.id : item1 === item2;
  }

  cancel() {
    this.onCancel.emit();
  }

  private removeFirst<T>(array: T[], toRemove: T): void {
    const index = array.indexOf(toRemove);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }
  removeItem(
    selectedList: WritableSignal<Array<{ id: string }>>,
    item: { id: string }
  ) {
    selectedList.update((prev) => prev.filter((a) => a.id !== item.id));
    if (selectedList === this.selectedDevices) {
      this.selectedDeviceIds.update((prev) => prev.filter(id => id !== item.id));
    }
  }
  onAdd(
    event: MatChipInputEvent,
    filteredItems: Signal<{ id: string; name: string }[]>,
    selectedItems: WritableSignal<{ id: string }[]>,
    query: ModelSignal<string | undefined>
  ): void {
    const value = (event.value || '').trim().toLowerCase();
    const match = filteredItems().find((a) => a.name.toLowerCase() === value);
    if (match) {
      const previousValue = selectedItems();
      selectedItems.update((prev) => {
        return prev;
      });
      if (previousValue === selectedItems()) {
        return;
      }
      event.chipInput?.clear();
      query.set('');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  get criteriaControl(): FormControl {
    return this.form.get('assets.criteria') as FormControl;
  }

  get selectedCriteria(): string | null {
    return this.activeCriteria();
  }

  get groupsControl(): FormControl {
    return this.form.get('assets.groupsCriteria') as FormControl;
  }

  get propertyControl(): FormControl {
    return this.form.get('assets.propertiesCriteria') as FormControl;
  }

  filterDevices() {
    const selectedGroupId: string | null =
      this.form.controls.assets.controls.groupsCriteria.value;
    const selectedPropertyId: string | null =
      this.form.controls.assets.controls.propertiesCriteria.value;

    let filtered: Device[] = [];
    if (selectedGroupId && !selectedPropertyId) {
      filtered = this.allDevices().filter(
        (device) => {
          const groups = (device as any).groups || device.groupsCriteria;
          return groups && groups.some((g: any) => g.id === selectedGroupId);
        }
      );
    } else if (
      selectedPropertyId && !selectedGroupId
    ) {
      filtered = this.allDevices().filter(
        (device) => {
          return device.property && device.property.id === selectedPropertyId;
        }
      );
    } else {
      filtered = this.allDevices().slice();
    }

    const normalized = filtered.map((d) => ({
      ...d,
      __typename: 'Device' as 'Device',
      property: d.property
  ? { ...d.property, __typename: 'PropertyWithConsumption' as 'PropertyWithConsumption' }
  : { __typename: 'PropertyWithConsumption' as 'PropertyWithConsumption', name: '', id: '' },
      groupsCriteria: Array.isArray(d.groupsCriteria)
        ? d.groupsCriteria.map((g) => ({ ...g, __typename: 'Group' as 'Group' }))
        : Array.isArray((d as any).groups)
        ? (d as any).groups.map((g: any) => ({ ...g, __typename: 'Group' as 'Group' }))
        : [],
      category: d.category
        ? { ...d.category, __typename: 'Category' as 'Category' }
        : { __typename: 'Category' as 'Category', name: '', id: '' },
      communicationProtocol: d.communicationProtocol
        ? {
            ...d.communicationProtocol,
            __typename: 'CommunicationProtocol' as 'CommunicationProtocol',
          }
        : {
            __typename: 'CommunicationProtocol' as 'CommunicationProtocol',
            name: '',
            id: '',
          },
    }));

    this.devices.set(normalized);
    this.filteredDevices.set(normalized);
    
    const selectedDeviceIds =
      this.form.controls.assets.controls.assets.value || [];
    const validSelectedDevices = normalized.filter((fd) =>
      selectedDeviceIds.includes(fd.id)
    );

    this.form.controls.assets.controls.assets.setValue(
      validSelectedDevices.map((d) => d.id)
    );
  }

  onSelect(
    event: MatAutocompleteSelectedEvent,
    selectedList: WritableSignal<Array<{ id: string }>>,
    query: ModelSignal<string | undefined> | any,
    autocompleteRef: HTMLInputElement
  ): void {
    const selected = event.option.value;
    if (selected && selected.id) {
      if (selectedList === this.selectedProperties) {
        
        this.form.controls.assets.controls.propertiesCriteria.setValue(selected.id);
       
        this.form.controls.assets.controls.groupsCriteria.setValue(null);
      } else if (selectedList === this.selectedGroups) {
      
        this.form.controls.assets.controls.groupsCriteria.setValue(selected.id);
      
        this.form.controls.assets.controls.propertiesCriteria.setValue(null);
      } else if (selectedList === this.selectedDevices) {
        const current = this.form.controls.assets.controls.assets.value ?? [];
        if (!current.includes(selected.id)) {
          this.form.controls.assets.controls.assets.setValue([
            ...current,
            selected.id,
          ]);
        }
     
        const selectedDevicesList = this.selectedDevices();
        if (!selectedDevicesList.some((d) => d.id === selected.id)) {
          this.selectedDevices.set([...selectedDevicesList, selected]);
          this.selectedDeviceIds.update((prev) => 
            prev.includes(selected.id) ? prev : [...prev, selected.id]
          );
        }
      }
    }
    autocompleteRef.value = '';
    if (query && typeof query.set === 'function') {
      query.set('');
    }
  }
}
